import { motion } from "framer-motion"
import { 
  ShieldCheck, Award, FileText, CheckCircle2, Globe2, Zap, Lock 
} from "lucide-react"

export function LiveTradeCommandCenter() {
  const trustContainerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  }

  const trustItemVariants = {
    hidden: { opacity: 0, y: 15 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        type: "spring" as const, 
        stiffness: 160, 
        damping: 18 
      } 
    },
  }

  return (
    <section className="relative bg-surface text-on-surface py-14 md:py-20 overflow-hidden border-t border-outline-variant/40 font-sans">
      {/* Background decoration */}
      <div 
        className="absolute inset-0 opacity-[0.25] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(var(--outline-variant) 1px, transparent 1px),
            linear-gradient(90deg, var(--outline-variant) 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px"
        }}
      />

      {/* Ambient background glows */}
      <div className="absolute right-[-10%] top-[-10%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[140px] pointer-events-none" />
      <div className="absolute left-[-10%] top-[10%] w-[600px] h-[600px] rounded-full bg-blue-400/10 blur-[150px] pointer-events-none" />

      {/* Light Transit Lines (Left Section) */}
      <div className="absolute left-0 top-0 bottom-0 w-80 md:w-[450px] pointer-events-none z-0 overflow-hidden opacity-40">
        <svg className="w-full h-full" viewBox="0 0 450 800" fill="none" preserveAspectRatio="none">
          <defs>
            <linearGradient id="leftTransitGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#be001e" stopOpacity="0.6" />
              <stop offset="40%" stopColor="#ef4444" stopOpacity="0.4" />
              <stop offset="80%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </linearGradient>
            <linearGradient id="leftTransitGrad2" x1="0%" y1="20%" x2="100%" y2="80%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
              <stop offset="60%" stopColor="#60a5fa" stopOpacity="0.25" />
              <stop offset="100%" stopColor="transparent" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Primary Transit Line */}
          <path
            d="M -40 -20 C 180 220, 80 500, 360 820"
            stroke="url(#leftTransitGrad1)"
            strokeWidth="2"
            strokeDasharray="6 6"
          />

          {/* Secondary Light Transit Line */}
          <path
            d="M -80 100 C 120 320, 40 580, 280 850"
            stroke="url(#leftTransitGrad2)"
            strokeWidth="1.5"
            strokeDasharray="10 6"
          />

          {/* Soft Accent Flow Line */}
          <path
            d="M -10 200 C 220 380, 120 620, 420 800"
            stroke="url(#leftTransitGrad1)"
            strokeWidth="1"
            strokeOpacity="0.4"
          />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-14">
          <motion.h2 
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-3xl md:text-5xl lg:text-6xl font-black text-on-surface tracking-tight leading-tight"
          >
            Source Better.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#be001e] to-red-500">
              Trade Globally.
            </span>
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-secondary text-sm md:text-base leading-relaxed font-normal max-w-xl mx-auto"
          >
            Connect with pre-audited manufacturers, lock escrow terms, and monitor cross-border shipments seamlessly across major global trade lanes.
          </motion.p>
        </div>

        {/* 4 Pillars Trust Indicators */}
        <motion.div 
          variants={trustContainerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          className="grid grid-cols-1 min-[460px]:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 w-full justify-center"
        >
          {/* Indicator 1: Verified Suppliers */}
          <motion.div 
            variants={trustItemVariants}
            whileHover={{ y: -3 }}
            className="flex items-center justify-center gap-2.5 bg-white border border-outline-variant/70 hover:border-primary/50 hover:shadow-md px-4 py-3.5 rounded-xl transition-all duration-200 cursor-pointer relative overflow-hidden group shadow-xs"
          >
            <CheckCircle2 className="size-4.5 text-primary shrink-0 transition-transform group-hover:scale-110" />
            <span className="text-xs text-on-surface/90 group-hover:text-primary font-bold uppercase tracking-wider transition-colors duration-200 truncate">
              Verified Suppliers
            </span>
          </motion.div>

          {/* Indicator 2: Escrow Trade */}
          <motion.div 
            variants={trustItemVariants}
            whileHover={{ y: -3 }}
            className="flex items-center justify-center gap-2.5 bg-white border border-outline-variant/70 hover:border-primary/50 hover:shadow-md px-4 py-3.5 rounded-xl transition-all duration-200 cursor-pointer relative overflow-hidden group shadow-xs"
          >
            <Award className="size-4.5 text-primary shrink-0 transition-transform group-hover:scale-110" />
            <span className="text-xs text-on-surface/90 group-hover:text-primary font-bold uppercase tracking-wider transition-colors duration-200 truncate">
              Escrow Trade
            </span>
          </motion.div>

          {/* Indicator 3: Global Logistics */}
          <motion.div 
            variants={trustItemVariants}
            whileHover={{ y: -3 }}
            className="flex items-center justify-center gap-2.5 bg-white border border-outline-variant/70 hover:border-primary/50 hover:shadow-md px-4 py-3.5 rounded-xl transition-all duration-200 cursor-pointer relative overflow-hidden group shadow-xs"
          >
            <Globe2 className="size-4.5 text-primary shrink-0 transition-transform group-hover:scale-110" />
            <span className="text-xs text-on-surface/90 group-hover:text-primary font-bold uppercase tracking-wider transition-colors duration-200 truncate">
              Global Logistics
            </span>
          </motion.div>

          {/* Indicator 4: RFQ Sourcing */}
          <motion.div 
            variants={trustItemVariants}
            whileHover={{ y: -3 }}
            className="flex items-center justify-center gap-2.5 bg-white border border-outline-variant/70 hover:border-primary/50 hover:shadow-md px-4 py-3.5 rounded-xl transition-all duration-200 cursor-pointer relative overflow-hidden group shadow-xs"
          >
            <FileText className="size-4.5 text-primary shrink-0 transition-transform group-hover:scale-110" />
            <span className="text-xs text-on-surface/90 group-hover:text-primary font-bold uppercase tracking-wider transition-colors duration-200 truncate">
              RFQ Sourcing
            </span>
          </motion.div>
        </motion.div>

      </div>
    </section>
  )
}

